import { FormTemplateType } from './form-template';
import { Roles } from '../permissions/roles';

export enum FormStatusCode {
  ClientSubmitted = 1,
  Incomplete = 2,
  DirectorReview = 3,
  SupervisorReview = 4,
  Complete = 5,
  OngoingPlan = 6,
}

export interface FormStatusOption {
  value: FormStatusCode;
  name: string;
}

export const SUBMISSION_PENDING_STATUS_KEY = 'submission_pending' as const;

export type FormStatusDisplayKey = FormStatusCode | typeof SUBMISSION_PENDING_STATUS_KEY;

export const BASE_STATUS_OPTIONS: readonly FormStatusOption[] = [
  { value: FormStatusCode.Incomplete, name: 'Incomplete' },
  { value: FormStatusCode.DirectorReview, name: 'Director Review' },
  { value: FormStatusCode.SupervisorReview, name: 'Supervisor Review' },
  { value: FormStatusCode.Complete, name: 'Complete' },
];

export const ONGOING_PLAN_OPTION: Readonly<FormStatusOption> = {
  value: FormStatusCode.OngoingPlan,
  name: 'Ongoing Plan',
};

export type PersonalizedPlanCandidate = {
  type?: unknown;
  template?: { type?: unknown } | number | string | null;
};

export type EntityIdReference =
  | number
  | string
  | { id?: number | string | null }
  | null
  | undefined;

export type PersonalizedPlanPermissionCandidate = PersonalizedPlanCandidate & {
  assigned_provider?: EntityIdReference;
  is_deleted?: boolean;
  status?: number | string | null;
};

export type PlanStatusUser = {
  id?: number | string | null;
  role?: number | string | null;
} | null;

export function isPersonalizedPlan(
  candidate: PersonalizedPlanCandidate | null | undefined,
): boolean {
  if (!candidate) {
    return false;
  }

  const templateType =
    candidate.type ??
    (typeof candidate.template === 'object' && candidate.template !== null
      ? candidate.template.type
      : undefined);

  return templateType === FormTemplateType.PersonalizedPlan;
}

export function getFormTemplateTypeLabel(
  candidate: PersonalizedPlanCandidate | null | undefined,
): string {
  if (!candidate) {
    return '';
  }

  const templateType =
    candidate.type ??
    (typeof candidate.template === 'object' && candidate.template !== null
      ? candidate.template.type
      : undefined);

  switch (Number(templateType)) {
    case FormTemplateType.Pdf:
      return 'PDF Form';
    case FormTemplateType.Fillable:
      return 'Fillable Form';
    case FormTemplateType.PersonalizedPlan:
      return 'Personalized Plan';
    default:
      return '';
  }
}

export function canOfferOngoingPlan(
  items: readonly PersonalizedPlanCandidate[] | null | undefined,
  featureEnabled: boolean,
): boolean {
  if (!featureEnabled || !items?.length) {
    return false;
  }

  return items.every(isPersonalizedPlan);
}

export function filterPersonalizedPlanTemplates<T extends PersonalizedPlanCandidate>(
  templates: readonly T[],
  featureEnabled: boolean,
): T[] {
  return featureEnabled
    ? [...templates]
    : templates.filter((template) => !isPersonalizedPlan(template));
}

export function getEntityId(value: EntityIdReference): number | null {
  const rawId = typeof value === 'object' && value !== null ? value.id : value;

  if (rawId === null || rawId === undefined || rawId === '') {
    return null;
  }

  const id = Number(rawId);

  return Number.isFinite(id) ? id : null;
}

export function canChangePersonalizedPlanStatus(
  form: PersonalizedPlanPermissionCandidate | null | undefined,
  user: PlanStatusUser,
): boolean {
  if (!form || !isPersonalizedPlan(form)) {
    return true;
  }

  const assignedProviderId = getEntityId(form.assigned_provider);

  if (assignedProviderId === null) {
    return true;
  }

  const role = Number(user?.role);
  const hasAdminAccess = role === Roles.Admin || role === Roles.SuperAdmin;

  return hasAdminAccess || getEntityId(user) === assignedProviderId;
}

export function canCustomizePersonalizedPlan(
  form: PersonalizedPlanPermissionCandidate | null | undefined,
  user: PlanStatusUser,
): boolean {
  if (
    !form ||
    !isPersonalizedPlan(form) ||
    form.is_deleted ||
    Number(form.status) === FormStatusCode.Complete ||
    !user
  ) {
    return false;
  }

  const userId = getEntityId(user);
  const role = Number(user.role);
  if (
    userId === null ||
    !Number.isSafeInteger(userId) ||
    userId <= 0 ||
    ![Roles.SuperAdmin, Roles.Admin, Roles.Manager, Roles.Employee].includes(role)
  ) {
    return false;
  }

  return canChangePersonalizedPlanStatus(form, user);
}

export function canFillPersonalizedPlan(
  form:
    | (PersonalizedPlanPermissionCandidate & { status?: number | string | null })
    | null
    | undefined,
  user: PlanStatusUser,
): boolean {
  if (!form || !isPersonalizedPlan(form)) {
    return true;
  }

  if (!user || Number(form.status) === FormStatusCode.Complete) {
    return false;
  }

  const role = Number(user.role);
  if (role === Roles.Admin || role === Roles.SuperAdmin) {
    return true;
  }

  const assignedProviderId = getEntityId(form.assigned_provider);

  return assignedProviderId === null || getEntityId(user) === assignedProviderId;
}

export function canChangePersonalizedPlanStatuses(
  forms: readonly PersonalizedPlanPermissionCandidate[] | null | undefined,
  user: PlanStatusUser,
): boolean {
  return Boolean(
    forms?.length && forms.every((form) => canChangePersonalizedPlanStatus(form, user)),
  );
}

export function getStatusOptions(options: { includeOngoingPlan: boolean }): FormStatusOption[] {
  return options.includeOngoingPlan
    ? [...BASE_STATUS_OPTIONS, ONGOING_PLAN_OPTION]
    : [...BASE_STATUS_OPTIONS];
}

export function getFormStatusDisplayKey(
  status: number | null | undefined,
  sharedWithPatient = false,
): FormStatusDisplayKey | null {
  if (status === FormStatusCode.Incomplete && sharedWithPatient) {
    return SUBMISSION_PENDING_STATUS_KEY;
  }

  return Object.values(FormStatusCode).includes(status as FormStatusCode)
    ? (status as FormStatusCode)
    : null;
}

export function getFormStatusLabel(
  status: number | null | undefined,
  sharedWithPatient = false,
): string {
  const displayKey = getFormStatusDisplayKey(status, sharedWithPatient);

  switch (displayKey) {
    case SUBMISSION_PENDING_STATUS_KEY:
      return 'Submission Pending';
    case FormStatusCode.ClientSubmitted:
      return 'Client Submitted';
    case FormStatusCode.Incomplete:
      return 'Incomplete';
    case FormStatusCode.DirectorReview:
      return 'Director Review';
    case FormStatusCode.SupervisorReview:
      return 'Supervisor Review';
    case FormStatusCode.Complete:
      return 'Complete';
    case FormStatusCode.OngoingPlan:
      return 'Ongoing Plan';
    default:
      return '';
  }
}
